import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { useFlash } from '../kit.js';
import { CameraView } from './camera-tile.js';
import { PtzPad, TalkButton } from './controls.js';
import {
    BackIcon, ForwardIcon, FullscreenExitIcon, SnapshotIcon, ZoomInIcon, ZoomOutIcon
} from './icons.js';
import { overlayAbove } from './media.js';
import { Timeline } from './timeline.js';

const EASE = [0.16, 1, 0.3, 1];
const CENTER = { x: 0, y: 0 };
const MAX_ZOOM = 4;
const PTZ_STEP = 9;      // percent of the frame per press
const PTZ_FRAME = 1.5;   // the zoom a PTZ move frames to, so it has room to travel

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/* Pan is in percent of the frame. At a zoom of z the picture overhangs
   the frame by (z - 1) / 2 of its size on each side, which is as far as
   it may move before an edge shows. */
function clampPan(pan, zoom) {
    const limit = (zoom - 1) * 50;
    return { x: clamp(pan.x, -limit, limit), y: clamp(pan.y, -limit, limit) };
}

/**
 * One camera, full screen: the staff viewer and the residents' viewer
 * are the same component with different `features`.
 *
 * It is drawn into <body> as a fixed overlay (a transformed screen
 * cannot contain a fixed element), and the screen that opens it also
 * asks the browser for real full screen. Where that is refused this
 * overlay is the whole of it, so the viewer behaves the same on an
 * iPhone as on the lobby display.
 *
 *   features: { ptz, snapshot, playback, talk }   all off by default
 *
 * Digital zoom works on every camera: the buttons, the wheel, a double
 * tap on a point, then drag to look around. A PTZ camera adds the pad,
 * which glides the frame rather than jumping it. With no zoom, a swipe
 * or the arrows step to the next camera.
 */
export function CameraViewer({
    cameras,
    cameraId,
    onNavigate,
    onClose,
    eyebrow = 'Camera',
    features = {},
    variant = 'full',
    statusOf = () => 'live',
    sinceOf = () => undefined,
    onReconnect,
    playbackOf = () => null,
    onPlayback,
    eventsOf = () => [],
    gapsOf = () => [],
    motionOf = () => null,
    talkingId = null,
    onTalk,
    onSnapshot,
    info
}) {
    const camera = cameras.find(c => c.id === cameraId) || null;
    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {camera && (
                <ViewerBody
                    key="viewer"
                    camera={camera}
                    cameras={cameras}
                    onNavigate={onNavigate}
                    onClose={onClose}
                    eyebrow={eyebrow}
                    features={features}
                    variant={variant}
                    statusOf={statusOf}
                    sinceOf={sinceOf}
                    onReconnect={onReconnect}
                    playbackOf={playbackOf}
                    onPlayback={onPlayback}
                    eventsOf={eventsOf}
                    gapsOf={gapsOf}
                    motionOf={motionOf}
                    talkingId={talkingId}
                    onTalk={onTalk}
                    onSnapshot={onSnapshot}
                    info={info}
                />
            )}
        </AnimatePresence>,
        document.body
    );
}

function ViewerBody({
    camera, cameras, onNavigate, onClose, eyebrow, features, variant,
    statusOf, sinceOf, onReconnect, playbackOf, onPlayback, eventsOf, gapsOf,
    motionOf, talkingId, onTalk, onSnapshot, info
}) {
    const reduced = useReducedMotion();
    const [flash, notify] = useFlash();
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState(CENTER);
    const [dragging, setDragging] = useState(false);
    const [flashKey, setFlashKey] = useState(0);
    const feeds = useRef({});
    const gesture = useRef(null);

    const index = Math.max(0, cameras.findIndex(c => c.id === camera.id));
    const status = statusOf(camera.id);
    const playback = playbackOf(camera.id);
    const ptz = features.ptz && camera.ptz;

    /* Each camera opens framed as its installer left it. */
    useEffect(() => {
        setZoom(1);
        setPan(CENTER);
    }, [camera.id]);

    const step = useCallback(delta => {
        if (cameras.length < 2) return;
        const next = cameras[(index + delta + cameras.length) % cameras.length];
        onNavigate?.(next.id);
    }, [cameras, index, onNavigate]);

    const zoomTo = useCallback(next => {
        const z = clamp(Math.round(next * 4) / 4, 1, MAX_ZOOM);
        setZoom(z);
        setPan(p => (z === 1 ? CENTER : clampPan(p, z)));
    }, []);

    const move = useCallback((dx, dy) => {
        const z = Math.max(zoom, PTZ_FRAME);
        if (z !== zoom) setZoom(z);
        setPan(p => clampPan({ x: p.x - dx * PTZ_STEP, y: p.y - dy * PTZ_STEP }, z));
    }, [zoom]);

    const home = useCallback(() => { setZoom(1); setPan(CENTER); }, []);

    const snap = useCallback(() => {
        const feed = feeds.current[camera.id];
        const shot = status === 'live' && feed ? feed.capture() : null;
        if (shot) setFlashKey(k => k + 1);
        const message = onSnapshot?.(camera, shot);
        if (message) notify(message);
    }, [camera, status, onSnapshot, notify]);

    const talk = useCallback(() => {
        const message = onTalk?.(camera);
        if (message) notify(message);
    }, [camera, onTalk, notify]);

    /* Escape leaves; arrows look around when zoomed and step through
       the cameras when not; + and - zoom; 0 goes home. */
    useEffect(() => {
        const onKey = event => {
            if (overlayAbove()) return;
            const target = event.target;
            const tag = target && target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            /* The timeline takes its own arrow keys when it has focus. */
            if (event.key !== 'Escape' && target && target.closest && target.closest('[role="slider"]')) return;
            const looking = zoom > 1 || ptz;
            if (event.key === 'Escape') onClose?.();
            else if (event.key === 'ArrowLeft') looking ? move(-1, 0) : step(-1);
            else if (event.key === 'ArrowRight') looking ? move(1, 0) : step(1);
            else if (event.key === 'ArrowUp' && looking) move(0, -1);
            else if (event.key === 'ArrowDown' && looking) move(0, 1);
            else if (event.key === '+' || event.key === '=') zoomTo(zoom + 0.5);
            else if (event.key === '-' || event.key === '_') zoomTo(zoom - 0.5);
            else if (event.key === '0') home();
            else return;
            event.preventDefault();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [zoom, ptz, move, step, zoomTo, home, onClose]);

    /* One pointer does three things: drag to pan when zoomed, swipe to
       the next camera when not, and a double tap zooms on a point. */
    const onPointerDown = event => {
        if (event.target.closest('button')) return;
        const rect = event.currentTarget.getBoundingClientRect();
        gesture.current = {
            id: event.pointerId, x: event.clientX, y: event.clientY,
            pan, w: rect.width, h: rect.height, moved: false
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = event => {
        const g = gesture.current;
        if (!g || g.id !== event.pointerId) return;
        const dx = event.clientX - g.x;
        const dy = event.clientY - g.y;
        if (!g.moved && Math.hypot(dx, dy) > 5) {
            g.moved = true;
            if (zoom > 1) setDragging(true);
        }
        if (g.moved && zoom > 1) {
            setPan(clampPan({ x: g.pan.x + (dx / g.w) * 100, y: g.pan.y + (dy / g.h) * 100 }, zoom));
        }
    };

    const onPointerUp = event => {
        const g = gesture.current;
        gesture.current = null;
        setDragging(false);
        if (!g) return;
        const dx = event.clientX - g.x;
        const dy = event.clientY - g.y;
        if (zoom === 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
    };

    const onDoubleClick = event => {
        if (event.target.closest('button')) return;
        if (zoom > 1) return home();
        const rect = event.currentTarget.getBoundingClientRect();
        const fx = (event.clientX - rect.left) / rect.width - 0.5;
        const fy = (event.clientY - rect.top) / rect.height - 0.5;
        const z = 2;
        setZoom(z);
        setPan(clampPan({ x: -fx * z * 100, y: -fy * z * 100 }, z));
    };

    const onWheel = event => {
        if (Math.abs(event.deltaY) < 4) return;
        zoomTo(zoom + (event.deltaY < 0 ? 0.25 : -0.25));
    };

    const screenClasses = ['cam-viewer__screen'];
    if (zoom > 1) screenClasses.push('cam-viewer__screen--zoomed');
    if (dragging) screenClasses.push('cam-viewer__screen--dragging');

    const meta = variant === 'soft'
        ? camera.location
        : `${camera.location} · ${camera.id.toUpperCase()}${camera.ptz ? ' · PTZ' : ''}`;

    /* The minimap's window, in the same terms the snapshot crops by. */
    const view = {
        left: (0.5 - (0.5 + pan.x / 100) / zoom) * 100,
        top: (0.5 - (0.5 + pan.y / 100) / zoom) * 100,
        size: 100 / zoom
    };

    return (
        <motion.div
            className="cam-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={`${camera.name}, full screen`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
        >
            <motion.div
                className="cam-viewer__inner screen-content"
                initial={reduced ? false : { opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5, ease: EASE }}
            >
                <header className="cam-viewer__head">
                    <div className="cam-viewer__titles">
                        <span className="screen__eyebrow">{eyebrow} · {index + 1} of {cameras.length}</span>
                        <h3 className="cam-viewer__title">{camera.name}</h3>
                        <span className="cam-viewer__meta">{meta}</span>
                    </div>
                    {info && <div className="cam-viewer__info">{info(camera)}</div>}
                    <button type="button" className="cam-viewer__exit" onClick={onClose} autoFocus>
                        <FullscreenExitIcon aria-hidden="true" />
                        <span>Exit</span>
                    </button>
                </header>

                <div className="cam-viewer__stage">
                    <div
                        className={screenClasses.join(' ')}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={() => { gesture.current = null; setDragging(false); }}
                        onDoubleClick={onDoubleClick}
                        onWheel={onWheel}
                    >
                        <AnimatePresence initial={false} mode="popLayout">
                            <motion.div
                                key={camera.id}
                                className="cam-viewer__feed"
                                initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.03 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.45, ease: EASE }}
                            >
                                <CameraView
                                    ref={el => {
                                        if (el) feeds.current[camera.id] = el;
                                        else delete feeds.current[camera.id];
                                    }}
                                    camera={camera}
                                    status={status}
                                    since={sinceOf(camera.id)}
                                    onReconnect={onReconnect ? id => { const m = onReconnect(id); if (m) notify(m); } : undefined}
                                    playback={playback}
                                    motion={motionOf(camera.id)}
                                    talking={talkingId === camera.id}
                                    flashKey={flashKey}
                                    variant={variant}
                                    priority={10}
                                    zoom={zoom}
                                    pan={pan}
                                    smooth={!dragging}
                                />
                            </motion.div>
                        </AnimatePresence>

                        {zoom > 1 && status === 'live' && (
                            <div className="cam-viewer__minimap" aria-hidden="true">
                                <img src={camera.still} alt="" draggable={false} />
                                <span style={{ left: `${view.left}%`, top: `${view.top}%`, width: `${view.size}%`, height: `${view.size}%` }} />
                            </div>
                        )}
                        {zoom > 1 && <span className="cam-viewer__hint">{zoom.toFixed(1)}x · drag to look around</span>}
                        <div className="cam-viewer__flash">{flash}</div>
                    </div>

                    {cameras.length > 1 && (
                        <>
                            <button type="button" className="cam-viewer__side cam-viewer__side--prev" onClick={() => step(-1)} aria-label="Previous camera">
                                <BackIcon aria-hidden="true" />
                            </button>
                            <button type="button" className="cam-viewer__side cam-viewer__side--next" onClick={() => step(1)} aria-label="Next camera">
                                <ForwardIcon aria-hidden="true" />
                            </button>
                        </>
                    )}
                </div>

                {/* The dock and the timeline travel together, so a phone
                    can centre the picture on screen with both below it. */}
                <div className="cam-viewer__controls">
                <div className="cam-viewer__dock">
                    {cameras.length > 1 && (
                        <div className="cam-viewer__group cam-viewer__nav">
                            <button type="button" className="cam-ibtn" onClick={() => step(-1)} aria-label="Previous camera"><BackIcon aria-hidden="true" /></button>
                            <button type="button" className="cam-ibtn" onClick={() => step(1)} aria-label="Next camera"><ForwardIcon aria-hidden="true" /></button>
                        </div>
                    )}

                    <div className="cam-viewer__group cam-zoom" role="group" aria-label="Zoom">
                        <button type="button" className="cam-ibtn" onClick={() => zoomTo(zoom - 0.5)} disabled={zoom <= 1} aria-label="Zoom out">
                            <ZoomOutIcon aria-hidden="true" />
                        </button>
                        <span className="cam-zoom__value" aria-live="polite">{zoom.toFixed(1)}x</span>
                        <button type="button" className="cam-ibtn" onClick={() => zoomTo(zoom + 0.5)} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">
                            <ZoomInIcon aria-hidden="true" />
                        </button>
                    </div>

                    {ptz && (
                        <PtzPad
                            onMove={move}
                            onHome={home}
                            readout={`Pan ${Math.round(-pan.x * 1.8)}° · Tilt ${Math.round(pan.y * 1.2)}°`}
                        />
                    )}

                    {(features.snapshot || features.talk) && (
                        <div className="cam-viewer__group">
                            {features.snapshot && (
                                <button type="button" className="cam-ibtn cam-ibtn--lg" onClick={snap} aria-label="Take snapshot" title="Snapshot">
                                    <SnapshotIcon aria-hidden="true" />
                                </button>
                            )}
                            {features.talk && <TalkButton on={talkingId === camera.id} onClick={talk} compact />}
                        </div>
                    )}
                </div>

                {features.playback && (
                    <Timeline
                        label={camera.name}
                        playback={playback}
                        onChange={next => onPlayback?.(camera.id, next)}
                        events={eventsOf(camera.id)}
                        gaps={gapsOf(camera.id)}
                    />
                )}
                </div>
            </motion.div>
        </motion.div>
    );
}
